import React, { useState, useEffect, useRef } from 'react';
import './HelpPage.css';

/* ─── Documentation data ─── */
const sections = [
  {
    category: 'Getting Started',
    topics: [
      {
        id: 'what-is-numfree',
        title: 'What is NumFree?',
        content: (
          <>
            <p>
              NumFree is an AI Phone — real numbers, mobile data, and AI.
              No physical SIM required.
              without requiring a physical SIM card or a dedicated app. Everything works over WiFi or your
              existing data connection.
            </p>
            <p>With NumFree you can:</p>
            <ul>
              <li>Make and receive phone calls from your browser</li>
              <li>Send and receive SMS / text messages</li>
              <li>Forward calls and texts to your existing phone(s)</li>
              <li>Get voicemail with automatic transcription</li>
              <li>Use multiple virtual numbers from one account</li>
            </ul>
            <div className="help-tip">
              <span className="help-callout-title">Tip</span>
              NumFree numbers work just like regular phone numbers. People calling you won't know the difference.
            </div>
          </>
        ),
      },
      {
        id: 'setting-up',
        title: 'Setting Up Your Number',
        content: (
          <>
            <p>After creating your account:</p>
            <ol>
              <li>Go to the <strong>Numbers</strong> tab to browse and select a phone number</li>
              <li>Choose a number from available US area codes</li>
              <li>Your number is ready to use immediately for browser calling and SMS</li>
            </ol>
            <h3>Setting Up Call Forwarding</h3>
            <p>
              To receive calls on your existing phone, go to your <strong>Profile</strong> and
              enter your personal phone number in the <strong>First forward number</strong> field.
              Toggle it to <strong>On</strong>.
            </p>
            <p>
              You can also add a <strong>Second forward number</strong> if you have two phones. Both
              will ring simultaneously when someone calls your NumFree number.
            </p>
            <div className="help-note">
              <span className="help-callout-title">Note</span>
              Make sure to include the country code when entering forward numbers (e.g. 15551234567 for US numbers).
            </div>
          </>
        ),
      },
      {
        id: 'browser-calling',
        title: 'Browser Calling',
        content: (
          <>
            <p>
              NumFree lets you make and receive calls directly from your web browser using WebRTC
              technology &mdash; no app or plugin required.
            </p>
            <ol>
              <li>Sign in and go to the <strong>Dialer</strong> page</li>
              <li>Allow microphone access when your browser prompts you</li>
              <li>Enter the number you want to call and press the call button</li>
            </ol>
            <p>
              Incoming calls will ring in your browser if you are signed in. If you also have forwarding
              numbers set up, all destinations ring simultaneously &mdash; first one to pick up gets
              the call.
            </p>
            <div className="help-tip">
              <span className="help-callout-title">Tip</span>
              For best results, use a recent version of Google Chrome or Safari. Make sure your
              microphone volume is set appropriately and that your device won't go to sleep
              during a call.
            </div>
          </>
        ),
      },
    ],
  },
  {
    category: 'SMS Features',
    topics: [
      {
        id: 'receiving-sms',
        title: 'Receiving SMS',
        content: (
          <>
            <p>
              When someone sends a text to your NumFree number, it is handled in one of two ways
              depending on your forwarding setup:
            </p>
            <ul>
              <li>
                <strong>Forward numbers set:</strong> The message is forwarded to your phone(s) as an
                SMS. It arrives with a <code>Fr:</code> prefix showing who sent it, followed by
                the message. Example:
              </li>
            </ul>
            <div className="help-code-block">Fr: +15551234567: Hey, are you available?</div>
            <ul>
              <li>
                <strong>No forward numbers:</strong> The message is sent to your email address
                on file so you never miss it.
              </li>
            </ul>
            <p>
              You can also view all your messages in the <strong>SMS</strong> tab when signed in.
            </p>
            <div className="help-note">
              <span className="help-callout-title">Note</span>
              If both forwarding numbers are turned Off, incoming text messages will only be
              delivered via email.
            </div>
          </>
        ),
      },
      {
        id: 'sending-sms-command',
        title: 'Sending SMS via Command',
        content: (
          <>
            <p>
              You can send an SMS from your NumFree number <strong>using your existing phone</strong> by
              texting a command to your NumFree number. The recipient will see the message as coming
              from your NumFree number, not your personal number.
            </p>
            <h3>Command format</h3>
            <div className="help-code-block">do: txt PHONENUMBER Your message here</div>
            <h3>Example</h3>
            <p>To send "Are you ready to go hiking tomorrow?" to (555) 345-1212 in the US:</p>
            <div className="help-code-block">do: txt 15553451212 Are you ready to go hiking tomorrow?</div>
            <p>
              The recipient at (555) 345-1212 will receive "Are you ready to go hiking tomorrow?"
              and it will appear to come from your NumFree number.
            </p>
            <div className="help-tip">
              <span className="help-callout-title">Tip</span>
              You can also use <code>do: text</code> instead of <code>do: txt</code> &mdash; both work the same way.
            </div>
          </>
        ),
      },
      {
        id: 'sms-forwarding',
        title: 'SMS Forwarding',
        content: (
          <>
            <p>
              When someone sends an SMS to your NumFree number, it is automatically forwarded to
              the phone number(s) you configured in your profile.
            </p>
            <h3>How it works</h3>
            <ul>
              <li>The forwarded message arrives from your NumFree number (not the original sender)</li>
              <li>The body starts with <code>Fr: +1XXXXXXXXXX:</code> so you know who actually sent it</li>
              <li>If you have two forward numbers, both phones receive the message</li>
              <li>MMS (picture messages) are also forwarded when supported</li>
            </ul>
            <div className="help-note">
              <span className="help-callout-title">Note</span>
              The <code>Fr:</code> prefix is added automatically so you can identify the sender.
              Keep this in mind when reading incoming forwarded messages.
            </div>
          </>
        ),
      },
      {
        id: 'replying-to-sms',
        title: 'Replying to Forwarded SMS',
        content: (
          <>
            <p>
              When you receive a forwarded SMS on your phone, you can reply directly to it.
              NumFree automatically routes your reply back to the <strong>original sender</strong>,
              and it appears to come from your NumFree number.
            </p>
            <h3>How reply routing works</h3>
            <ol>
              <li>Someone texts your NumFree number</li>
              <li>You receive it on your phone with the <code>Fr:</code> prefix</li>
              <li>Simply reply to that message &mdash; type just your response text, no command needed</li>
              <li>NumFree sends your reply from your NumFree number to the original sender</li>
            </ol>
            <p>
              If you have two phones, you can reply from either device. The response always appears
              to come from the same NumFree number.
            </p>
          </>
        ),
      },
      {
        id: 'email-to-sms',
        title: 'Email to SMS',
        content: (
          <>
            <p>
              NumFree includes a built-in email gateway. You can send an SMS by sending an email
              to a special address based on the recipient's phone number and your NumFree domain.
            </p>
            <h3>How it works</h3>
            <p>
              Send an email to <code>PHONENUMBER@numfree.com</code> where PHONENUMBER is the
              destination number. The email body becomes the SMS text, and it's sent from your
              NumFree number.
            </p>
            <div className="help-tip">
              <span className="help-callout-title">Tip</span>
              This is useful when you want to send a text from your computer without opening the
              NumFree portal. You can also set up email-to-SMS from services like SendGrid or Mandrill
              for automated notifications.
            </div>
          </>
        ),
      },
    ],
  },
  {
    category: 'Voice Features',
    topics: [
      {
        id: 'receiving-calls',
        title: 'Receiving Calls',
        content: (
          <>
            <p>
              When someone calls your NumFree number, the call is routed to all your connected
              destinations simultaneously:
            </p>
            <ul>
              <li>Your browser (if you're signed in to the Dialer page)</li>
              <li>Your first forward number (if configured and enabled)</li>
              <li>Your second forward number (if configured and enabled)</li>
              <li>Your SIM card (if SIM integration is enabled)</li>
            </ul>
            <p>
              The first destination to answer gets the call. The caller sees your NumFree number
              as the number they dialed, and on your forwarded phone the original caller's number
              is displayed as the caller ID.
            </p>
          </>
        ),
      },
      {
        id: 'call-forwarding',
        title: 'Call Forwarding Setup',
        content: (
          <>
            <p>
              Call forwarding lets incoming calls to your NumFree number ring on your existing
              phone(s).
            </p>
            <h3>Setup steps</h3>
            <ol>
              <li>Go to your <strong>Profile</strong> page</li>
              <li>Enter your phone number in the <strong>First forward number</strong> field</li>
              <li>Toggle it to <strong>On</strong></li>
              <li>Optionally add a <strong>Second forward number</strong> for a second device</li>
            </ol>
            <p>
              Both forward numbers ring simultaneously. The call connects to whichever phone
              answers first. If nobody answers within 25 seconds, the caller is sent to voicemail.
            </p>
          </>
        ),
      },
      {
        id: 'making-calls-browser',
        title: 'Making Calls (Browser)',
        content: (
          <>
            <p>
              Use the <strong>Dialer</strong> page to place outgoing calls. Your NumFree number
              appears as the caller ID to the person you're calling.
            </p>
            <ol>
              <li>Go to the <strong>Dialer</strong> page</li>
              <li>Enter the phone number you want to call</li>
              <li>Click the call button</li>
            </ol>
            <p>
              The call is placed using your browser's microphone and speakers (WebRTC). Call
              history appears in the panel next to the dialer.
            </p>
          </>
        ),
      },
      {
        id: 'making-calls-sms',
        title: 'Making Calls via SMS Command',
        content: (
          <>
            <p>
              You can initiate a call through your NumFree number <strong>from your regular
              phone</strong> without the browser. This is useful when you want the person you're
              calling to see your NumFree number instead of your personal number.
            </p>
            <h3>Step 1: Send the command</h3>
            <p>From your phone, text the following to your NumFree number:</p>
            <div className="help-code-block">do: call 15553451212</div>
            <p>
              You'll receive a confirmation: "Next time you call [your NumFree number] you will be
              connected to: 15553451212"
            </p>
            <h3>Step 2: Call your NumFree number</h3>
            <p>
              Now call your NumFree number from your phone. Instead of going to voicemail, the call
              is forwarded to the number you specified. The recipient sees your NumFree number as
              the caller ID.
            </p>
            <div className="help-tip">
              <span className="help-callout-title">Tip</span>
              Traveling abroad? Get a local SIM card and use this feature to call back home through
              your NumFree number &mdash; avoid expensive international roaming charges.
            </div>
          </>
        ),
      },
      {
        id: 'call-back',
        title: 'Call-Back Feature',
        content: (
          <>
            <p>
              NumFree automatically tracks who called you. When you call your NumFree number from
              your registered forward number, it connects you to the <strong>last person who
              called</strong> that NumFree number.
            </p>
            <h3>How it works</h3>
            <ol>
              <li>Someone calls your NumFree number (you miss it or see the alert)</li>
              <li>From your phone (the forward number), call your NumFree number</li>
              <li>NumFree announces the number and connects you to the caller</li>
            </ol>
            <p>
              Your NumFree number shows as the caller ID, so the person you're calling back sees a
              consistent number from you.
            </p>
            <div className="help-note">
              <span className="help-callout-title">Note</span>
              If there are no recent callers, you'll hear a message saying "No recent calls to your
              number." If the last caller is in your blocked list, you'll be informed that the number
              is blocked.
            </div>
          </>
        ),
      },
      {
        id: 'voicemail',
        title: 'Voicemail & Transcription',
        content: (
          <>
            <p>
              If no one answers an incoming call within 25 seconds, the caller hears a voicemail
              prompt and can leave a message.
            </p>
            <h3>What you receive</h3>
            <ul>
              <li>An SMS alert to your forward number(s) with a link to the recording</li>
              <li>Automatic transcription of the voicemail included in the alert when available</li>
              <li>The original caller's number so you can call back</li>
            </ul>
            <p>
              Recording links are shortened for easy sharing. The transcription is generated
              automatically and included in the SMS notification so you can read the
              message without listening to the audio.
            </p>
          </>
        ),
      },
      {
        id: 'call-alerts',
        title: 'Incoming Call Alerts',
        content: (
          <>
            <p>
              When someone calls your NumFree number and you have call forwarding enabled, you
              receive an <strong>SMS alert</strong> on your phone <em>before</em> the call rings.
            </p>
            <p>
              This alert tells you which NumFree number is being called and who is calling, so you
              can decide whether to answer.
            </p>
            <div className="help-tip">
              <span className="help-callout-title">Tip</span>
              This is especially useful if you use your personal phone for multiple NumFree numbers.
              The SMS alert lets you know which number the caller dialed before you pick up.
            </div>
            <p>
              If no forward numbers are configured, you'll receive an email notification of the
              incoming call instead.
            </p>
          </>
        ),
      },
    ],
  },
  {
    category: 'Advanced',
    topics: [
      {
        id: 'number-blocking',
        title: 'Number Blocking',
        content: (
          <>
            <p>
              NumFree supports two levels of call blocking to protect you from spam and
              unwanted callers:
            </p>
            <h3>Personal block list</h3>
            <p>
              You can add specific phone numbers to your personal blocked number list. Calls from
              blocked numbers are automatically rejected without ringing. The caller hears a
              "rejected" signal.
            </p>
            <h3>Global block list</h3>
            <p>
              NumFree also maintains a system-wide blacklist of known spam numbers. Calls from
              these numbers receive a busy signal automatically.
            </p>
            <div className="help-note">
              <span className="help-callout-title">Note</span>
              The call-back feature will not connect you to a number that's on your block list.
              You'll hear a message saying the recent caller's number is blocked.
            </div>
          </>
        ),
      },
      {
        id: 'multiple-forward-numbers',
        title: 'Multiple Forward Numbers',
        content: (
          <>
            <p>
              NumFree supports up to two forward numbers per NumFree number. Both phones ring
              simultaneously for incoming calls and both receive forwarded SMS messages.
            </p>
            <h3>Use cases</h3>
            <ul>
              <li>Ring your personal phone and your work phone at the same time</li>
              <li>Share a business number between two people</li>
              <li>Have a backup phone that catches calls you might miss</li>
            </ul>
            <p>
              Each forward number can be independently toggled on or off without removing it from
              your profile.
            </p>
          </>
        ),
      },
      {
        id: 'sim-integration',
        title: 'SIM Card Integration',
        content: (
          <>
            <p>
              For users with a compatible IoT SIM card, NumFree can route calls and SMS directly to the SIM.
              When SIM integration is enabled, it takes priority over regular call forwarding.
            </p>
            <h3>How it works</h3>
            <ul>
              <li>Incoming calls are dialed to your SIM first</li>
              <li>If the SIM call completes, no further forwarding happens</li>
              <li>Incoming SMS can also be routed to the SIM</li>
              <li>Outbound SMS from the SIM uses your NumFree number as the sender</li>
            </ul>
            <div className="help-note">
              <span className="help-callout-title">Note</span>
              SIM integration requires a compatible IoT SIM card and must be enabled in your
              account settings. Contact support at help@numfree.com for setup assistance.
            </div>
          </>
        ),
      },
      {
        id: 'mms-support',
        title: 'MMS Support',
        content: (
          <>
            <p>
              NumFree supports MMS (picture messages) forwarding. When someone sends an image or
              media message to your NumFree number:
            </p>
            <ul>
              <li>The media is forwarded along with the text to your forward number(s)</li>
              <li>The <code>Fr:</code> prefix is still included so you know who sent it</li>
            </ul>
            <div className="help-note">
              <span className="help-callout-title">Note</span>
              MMS forwarding depends on carrier support. Some international carriers may not
              support receiving MMS from US numbers. In those cases, only the text portion of the
              message will be delivered.
            </div>
          </>
        ),
      },
      {
        id: 'support',
        title: 'Getting Support',
        content: (
          <>
            <p>
              If you need help with your NumFree account, reach out to us:
            </p>
            <ul>
              <li>Email: <strong>help@numfree.com</strong></li>
            </ul>
            <p>
              When contacting support, please include your NumFree number and a description of the
              issue so we can help you faster.
            </p>
          </>
        ),
      },
      {
        id: 'privacy',
        title: 'Privacy Policy',
        content: (
          <>
            <p>
              NumFree keeps your data private and does not release your personal information to
              third parties without your consent, unless legally obligated to do so.
            </p>
            <ul>
              <li>NumFree does not share your data for advertising purposes</li>
              <li>
                Call and message data cannot remain confidential once forwarded to external
                networks to reach non-NumFree recipients
              </li>
            </ul>
          </>
        ),
      },
    ],
  },
];

/* ─── Component ─── */
export default function HelpPage() {
  const [activeId, setActiveId] = useState(sections[0].topics[0].id);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const contentRef = useRef(null);

  /* Scroll to section on hash change or initial load */
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      setActiveId(hash);
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, []);

  const handleTopicClick = (id) => {
    setActiveId(id);
    setSidebarOpen(false);
    window.history.replaceState(null, '', `#${id}`);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  /* Track active section on scroll */
  useEffect(() => {
    const allIds = sections.flatMap((s) => s.topics.map((t) => t.id));

    const onScroll = () => {
      const container = contentRef.current;
      if (!container) return;

      for (let i = allIds.length - 1; i >= 0; i--) {
        const el = document.getElementById(allIds[i]);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 160) {
            setActiveId(allIds[i]);
            return;
          }
        }
      }
    };

    const container = contentRef.current;
    if (container) {
      container.addEventListener('scroll', onScroll, { passive: true });
      return () => container.removeEventListener('scroll', onScroll);
    }
  }, []);

  return (
    <div className="help-page">
      {/* Mobile overlay */}
      <div
        className={`help-sidebar-overlay${sidebarOpen ? ' visible' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <nav className={`help-sidebar${sidebarOpen ? ' open' : ''}`}>
        <div className="help-sidebar-header">
          <h3>Documentation</h3>
        </div>
        {sections.map((section) => (
          <div key={section.category}>
            <div className="help-sidebar-category">{section.category}</div>
            {section.topics.map((topic) => (
              <a
                key={topic.id}
                href={`#${topic.id}`}
                className={`help-sidebar-item${activeId === topic.id ? ' active' : ''}`}
                onClick={(e) => { e.preventDefault(); handleTopicClick(topic.id); }}
              >
                {topic.title}
              </a>
            ))}
          </div>
        ))}
      </nav>

      {/* Content */}
      <div className="help-content" ref={contentRef}>
        <h1>NumFree Help</h1>
        <p className="help-subtitle">
          Your AI Phone — numbers, data, and intelligence.
        </p>

        {sections.map((section) =>
          section.topics.map((topic) => (
            <div key={topic.id} id={topic.id} className="help-section">
              <h2>{topic.title}</h2>
              {topic.content}
            </div>
          ))
        )}
      </div>

      {/* Mobile toggle */}
      <button
        className="help-mobile-toggle"
        onClick={() => setSidebarOpen((prev) => !prev)}
        aria-label="Toggle help navigation"
      >
        {sidebarOpen ? '\u2715' : '\u2630'}
      </button>
    </div>
  );
}
